import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, Check, CheckCheck, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthLite } from '@/context/AuthLite';
import { cn } from '@/lib/utils';
import { queryKeys } from '@/lib/query-keys';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string | null;
  link: string | null;
  isRead: boolean;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

const POLL_INTERVAL = 60_000;
const NEW_LEAD_NOTIFICATION_TYPES = new Set(['facebook_lead', 'configurator_lead', 'cf7_lead']);

type NotificationTab = 'newLeads' | 'priority';

interface NotificationBellProps {
  variant?: 'sidebar' | 'header';
}

export default function NotificationBell({ variant = 'sidebar' }: NotificationBellProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuthLite();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const prevUnreadRef = useRef<number | null>(null);
  const dateLocale = i18n.language === 'sk' ? 'sk-SK' : i18n.language === 'hu' ? 'hu-HU' : 'en-GB';

  const notificationTitle = (n: Notification) => {
    const key = `notifications.types.${n.type}`;
    return i18n.exists(key) ? t(key) : n.title;
  };

  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<NotificationTab>('newLeads');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const { newLeadNotifications, priorityNotifications } = useMemo(() => {
    const newLeads: Notification[] = [];
    const priority: Notification[] = [];

    for (const notification of notifications) {
      if (NEW_LEAD_NOTIFICATION_TYPES.has(notification.type)) {
        newLeads.push(notification);
      } else {
        priority.push(notification);
      }
    }

    return {
      newLeadNotifications: newLeads,
      priorityNotifications: priority,
    };
  }, [notifications]);

  const activeNotifications =
    activeTab === 'newLeads' ? newLeadNotifications : priorityNotifications;
  const activeEmptyKey =
    activeTab === 'newLeads' ? 'notifications.emptyNewLeads' : 'notifications.emptyPriority';
  const hasUnreadNotifications = notifications.some((notification) => !notification.isRead);
  const showMarkAllRead = unreadCount > 0 || hasUnreadNotifications;

  const fetchUnreadCount = useCallback(async () => {
    if (!user?.email) return;
    try {
      const res = await fetch('/api/notifications/unread-count', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const next = data.count ?? 0;
        const prev = prevUnreadRef.current;
        prevUnreadRef.current = next;
        if (prev !== null && next > prev) {
          void qc.invalidateQueries({ queryKey: queryKeys.crmBoard });
        }
        setUnreadCount(next);
      }
    } catch {}
  }, [user?.email, qc]);

  const fetchNotifications = useCallback(async () => {
    if (!user?.email) return;
    setLoading(true);
    try {
      const res = await fetch('/api/notifications?limit=20', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications ?? []);
      }
    } catch {} finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const markRead = async (id: string) => {
    try {
      await fetch(`/api/notifications/${id}/read`, {
        method: 'PATCH',
        credentials: 'include',
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await fetch('/api/notifications/mark-all-read', {
        method: 'POST',
        credentials: 'include',
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleNotificationClick = (n: Notification) => {
    if (!n.isRead) markRead(n.id);
    if (n.link) {
      navigate(n.link);
      setOpen(false);
    }
  };

  if (!user?.email) return null;

  const isHeader = variant === 'header';

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'relative flex items-center justify-center rounded-lg transition-colors',
          isHeader
            ? 'w-10 h-10 text-foreground hover:bg-accent'
            : 'w-12 h-12 text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent'
        )}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="notification-badge-pulse absolute top-1.5 right-1.5 flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className={cn(
            'absolute w-[340px] max-h-[420px] bg-background text-foreground border rounded-lg shadow-lg z-[100] flex flex-col overflow-hidden',
            isHeader ? 'right-0 top-full mt-2' : 'left-full bottom-0 ml-2'
          )}
        >
          <div className="flex items-center justify-between p-3 border-b">
            <h3 className="text-sm font-semibold">{t('notifications.title')}</h3>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1 hover:bg-muted rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-1 p-2 border-b bg-muted/30">
            <button
              type="button"
              onClick={() => setActiveTab('newLeads')}
              className={cn(
                'rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                activeTab === 'newLeads'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-background/60 hover:text-foreground'
              )}
            >
              {t('notifications.tabs.newLeads')} ({newLeadNotifications.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('priority')}
              className={cn(
                'rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                activeTab === 'priority'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-background/60 hover:text-foreground'
              )}
            >
              {t('notifications.tabs.priority')} ({priorityNotifications.length})
            </button>
          </div>

          {showMarkAllRead && (
            <div className="border-b p-2">
              <Button
                variant="outline"
                size="sm"
                onClick={markAllRead}
                className="h-8 w-full justify-center gap-1.5 text-xs font-medium"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                {t('notifications.markAllRead')}
              </Button>
            </div>
          )}

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {t('common.loading')}
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {t('notifications.empty')}
              </div>
            ) : activeNotifications.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {t(activeEmptyKey)}
              </div>
            ) : (
              activeNotifications.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => handleNotificationClick(n)}
                  className={cn(
                    'w-full text-left px-3 py-2.5 border-b last:border-b-0 text-foreground hover:bg-muted/50 transition-colors',
                    !n.isRead && 'bg-primary/5'
                  )}
                >
                  <div className="flex items-start gap-2">
                    {!n.isRead && (
                      <span className="mt-1.5 w-2 h-2 rounded-full bg-primary shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={cn('text-sm', !n.isRead && 'font-medium')}>
                        {notificationTitle(n)}
                      </p>
                      {n.message && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {n.message}
                        </p>
                      )}
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {new Date(n.createdAt).toLocaleString(dateLocale)}
                      </p>
                    </div>
                    {!n.isRead && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          markRead(n.id);
                        }}
                        className="p-1.5 rounded-md shrink-0 border border-primary/30 bg-background text-primary hover:bg-primary/10"
                        title={t('notifications.markRead')}
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
